import { API_BASE_URL, ApiError, classifiedFetch, parseError, silentRefresh, apiFetch, type TokenStore } from './client.ts';

/**
 * Help Center API - issues, feature requests and experience feedback.
 * Submitting is open to any authenticated user; listing/triaging is platform_admin.
 * Attachments are sent base64 in JSON (the project convention - no multipart).
 */

export type FeedbackType = 'issue' | 'feature_request' | 'experience';
export type TicketFeedbackType = Exclude<FeedbackType, 'experience'>;
import type { FeedbackStatus } from '../lib/ticket-views.ts';
import { SIGNED_OUT_MESSAGE } from '../lib/error-labels.ts';
export type { FeedbackStatus };
export type FeedbackPriority = 'low' | 'medium' | 'high';

export interface FeedbackAttachment {
  id: string;
  fileObjectId: string;
  filename: string | null;
  mimeType: string | null;
  byteSize: number | null;
}

/** One message in a submission's thread. `authorName` is already display-safe -
 *  the server renders every platform admin as "HapMetrix support". */
export interface FeedbackComment {
  id: string;
  body: string;
  createdAt: number;
  authorIsSupport: boolean;
  authorName: string;
  /** Admin-only note. Only ever true on the admin triage view; the reporter's
   *  thread never contains internal comments (filtered server-side). */
  isInternal: boolean;
}

export interface FeedbackSubmission {
  id: string;
  /** Human-quotable handle (FB-0001). Null only on pre-migration rows. */
  reference: string;
  type: FeedbackType;
  subject: string;
  body: string;
  status: FeedbackStatus;
  priority: FeedbackPriority;
  /** 1-5 on experience feedback; null on issues and feature requests. */
  rating: number | null;
  submittedByUserId: string;
  practiceId: string | null;
  createdAt: number;
  updatedAt: number | null;
  resolvedAt: number | null;
  attachments: FeedbackAttachment[];
  comments: FeedbackComment[];
  /** Platform-admin triage list only - a reporter's own history omits these. */
  submitterName?: string;
  submitterEmail?: string;
  practiceName?: string | null;
}

/** Mirrors src/lib/upload-limits.ts. The server is authoritative; this lets the
 *  form reject oversized files before a wasted upload. */
export const MAX_FEEDBACK_FILE_BYTES = 25 * 1024 * 1024;
export const MAX_FEEDBACK_FILES = 5;
export const FEEDBACK_ACCEPT = '.pdf,.png,.jpg,.jpeg,.doc,.docx,application/pdf,image/png,image/jpeg';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string; // "data:<mime>;base64,XXXX"
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

export async function submitFeedback(
  input: { type: TicketFeedbackType; subject: string; body: string; priority: FeedbackPriority; files: File[] },
  store: TokenStore,
): Promise<{ submissionId: string }> {
  const attachments = await Promise.all(
    input.files.map(async (f) => ({
      filename: f.name,
      mimeType: f.type || 'application/octet-stream',
      contentBase64: await fileToBase64(f),
    })),
  );
  return apiFetch<{ submissionId: string }>(
    '/feedback/submissions',
    {
      method: 'POST',
      body: JSON.stringify({
        type: input.type,
        subject: input.subject,
        body: input.body,
        priority: input.priority,
        ...(attachments.length > 0 ? { attachments } : {}),
      }),
    },
    store,
  );
}

/** Experience feedback: a 1-5 rating and two optional answers (POST /feedback/experience). */
export async function submitExperienceFeedback(
  input: { rating: number; wentWell: string; couldBeBetter: string },
  store: TokenStore,
): Promise<{ submissionId: string }> {
  return apiFetch<{ submissionId: string }>(
    '/feedback/experience',
    { method: 'POST', body: JSON.stringify(input) },
    store,
  );
}

/** Whether the monthly experience-feedback pop-up is due for the signed-in person. */
export async function getFeedbackPrompt(store: TokenStore): Promise<{ due: boolean }> {
  return apiFetch<{ due: boolean }>('/me/feedback-prompt', { method: 'GET' }, store);
}

export async function snoozeFeedbackPrompt(store: TokenStore): Promise<void> {
  await apiFetch<{ recorded: boolean }>('/me/feedback-prompt/snooze', { method: 'POST' }, store);
}

export async function optOutOfFeedbackPrompt(store: TokenStore): Promise<void> {
  await apiFetch<{ recorded: boolean }>('/me/feedback-prompt/opt-out', { method: 'POST' }, store);
}

export async function listFeedback(store: TokenStore): Promise<FeedbackSubmission[]> {
  return apiFetch<FeedbackSubmission[]>('/feedback/submissions', { method: 'GET' }, store);
}

export async function listMyFeedback(store: TokenStore): Promise<FeedbackSubmission[]> {
  return apiFetch<FeedbackSubmission[]>('/feedback/submissions/mine', { method: 'GET' }, store);
}

export async function reopenFeedback(
  id: string,
  reason: string,
  store: TokenStore,
): Promise<FeedbackSubmission> {
  return apiFetch<FeedbackSubmission>(
    `/feedback/submissions/${encodeURIComponent(id)}/reopen`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

export async function updateFeedbackStatus(
  id: string,
  status: FeedbackStatus,
  store: TokenStore,
): Promise<FeedbackSubmission> {
  return apiFetch<FeedbackSubmission>(
    `/feedback/submissions/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify({ status }) },
    store,
  );
}

/** Append a reply to a submission's thread (submitter or platform_admin).
 *  Returns the submission with the updated thread. */
export async function addFeedbackComment(
  submissionId: string,
  body: string,
  store: TokenStore,
  internal = false,
): Promise<FeedbackSubmission> {
  return apiFetch<FeedbackSubmission>(
    `/feedback/submissions/${encodeURIComponent(submissionId)}/comments`,
    { method: 'POST', body: JSON.stringify({ body, internal }) },
    store,
  );
}

/**
 * Authenticated attachment download (invariant 14 - access goes through the API,
 * logged server-side; no storage_key exposed). The access token is in memory, so
 * we fetch the bytes with the bearer token and trigger a download - mirrors
 * downloadFileObject in api/files.ts.
 */
export async function downloadFeedbackAttachment(
  submissionId: string,
  fileObjectId: string,
  filename: string,
  store: TokenStore,
): Promise<void> {
  const path = `/feedback/submissions/${encodeURIComponent(submissionId)}/attachments/${encodeURIComponent(fileObjectId)}`;
  const fetchOnce = (token: string | null): Promise<Response> =>
    classifiedFetch(`${API_BASE_URL}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    }, { timeoutMs: 300_000 });

  let res = await fetchOnce(store.getToken());
  if (res.status === 401) {
    const refreshed = await silentRefresh();
    if (refreshed) {
      store.onRefresh(refreshed);
      res = await fetchOnce(refreshed.accessToken);
    } else {
      store.onAuthFailure();
    }
  }
  if (res.status === 401) {
    throw new ApiError(401, 'UNAUTHORIZED', SIGNED_OUT_MESSAGE);
  }
  if (!res.ok) {
    throw await parseError(res);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
